import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

const BASE_URL = 'https://speedtestry.com'

// Get the page key from pathname
function getPageKey(pathname: string): string {
  // Remove language prefix
  const pathWithoutLang = pathname.replace(/^\/zh/, '') || '/'

  // Map paths to SEO keys
  if (pathWithoutLang === '/' || pathWithoutLang === '') {
    return 'home'
  }
  if (pathWithoutLang === '/about') {
    return 'about'
  }
  if (pathWithoutLang === '/privacy') {
    return 'privacy'
  }
  if (pathWithoutLang === '/terms') {
    return 'terms'
  }
  if (pathWithoutLang === '/contact') {
    return 'contact'
  }
  // Default to home for unknown paths
  return 'home'
}

export const useSEO = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()

  useEffect(() => {
    const pageKey = getPageKey(location.pathname)

    // Update document title
    document.title = t(`seo.${pageKey}.title`)

    // Keep application data on the home route only, including SPA navigation.
    const existingSchema = document.getElementById('webapp-schema') as HTMLScriptElement | null
    if (pageKey !== 'home') {
      existingSchema?.remove()
    } else {
      const schema = existingSchema ?? document.createElement('script')
      schema.id = 'webapp-schema'
      schema.type = 'application/ld+json'
      schema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'SpeedTestry',
        url: BASE_URL,
        description: t('seo.home.description'),
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      })
      if (!existingSchema) document.head.appendChild(schema)
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', t(`seo.${pageKey}.description`))
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]')
    if (metaKeywords) {
      metaKeywords.setAttribute('content', t('seo.keywords'))
    }

    // Update Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) {
      ogTitle.setAttribute('content', t(`seo.${pageKey}.title`))
    }

    const ogDescription = document.querySelector('meta[property="og:description"]')
    if (ogDescription) {
      ogDescription.setAttribute('content', t(`seo.${pageKey}.description`))
    }

    const canonicalPath = location.pathname === '/zh' ? '/zh/' : location.pathname
    const pageUrl = `${BASE_URL}${canonicalPath}`
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', pageUrl)
    document.querySelector('meta[name="twitter:url"]')?.setAttribute('content', pageUrl)
    document.querySelector('meta[property="og:locale"]')?.setAttribute('content', i18n.language === 'zh' ? 'zh_CN' : 'en_US')

    // Update Twitter meta tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]')
    if (twitterTitle) {
      twitterTitle.setAttribute('content', t(`seo.${pageKey}.title`))
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]')
    if (twitterDescription) {
      twitterDescription.setAttribute('content', t(`seo.${pageKey}.description`))
    }

    // Update html lang attribute
    document.documentElement.lang = i18n.language === 'zh' ? 'zh-CN' : 'en'

    // Update hreflang tags
    updateHreflangTags(location.pathname)
  }, [t, i18n.language, location.pathname])
}

function updateHreflangTags(pathname: string) {
  // Remove existing hreflang tags
  const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]')
  existingHreflangs.forEach(tag => tag.remove())

  // Get the path without language prefix
  const pathWithoutLang = pathname.replace(/^\/zh/, '') || '/'
  
  // Build URLs for each language
  const enUrl = `${BASE_URL}${pathWithoutLang}`
  const zhUrl = `${BASE_URL}/zh${pathWithoutLang === '/' ? '/' : pathWithoutLang}`

  // Create and append hreflang tags
  const head = document.head

  // English (x-default)
  const xDefaultLink = document.createElement('link')
  xDefaultLink.rel = 'alternate'
  xDefaultLink.hreflang = 'x-default'
  xDefaultLink.href = enUrl
  head.appendChild(xDefaultLink)

  // English
  const enLink = document.createElement('link')
  enLink.rel = 'alternate'
  enLink.hreflang = 'en'
  enLink.href = enUrl
  head.appendChild(enLink)

  // Chinese
  const zhLink = document.createElement('link')
  zhLink.rel = 'alternate'
  zhLink.hreflang = 'zh'
  zhLink.href = zhUrl
  head.appendChild(zhLink)

  // Update canonical URL
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    head.appendChild(canonicalLink)
  }
  canonicalLink.href = pathname.startsWith('/zh') ? zhUrl : enUrl
}
